import React, { useEffect, useState } from 'react';
import Input from '../../components/common/Input/Input';
import Button from '../../components/common/Button/Button';
import { formatNumber } from '../../utils/formatters';
import { useNotification } from '../../context/NotificationContext';
import { Section, Loading } from './AdminUI';
import { fromWei, toWei, pctToBps, bpsToPct } from './useAdmin';

const TIME_PRESETS = [
  { label: '5 min', value: 300 },
  { label: '1 hour', value: 3600 },
  { label: '1 day', value: 86400 }
];

const isNum = (v) => v !== '' && !isNaN(parseFloat(v)) && parseFloat(v) >= 0;

const AdminSettings = ({ admin }) => {
  const { read, runTx, pending, isOwner } = admin;
  const { error: showError } = useNotification();
  const [form, setForm] = useState(null);
  const [balance, setBalance] = useState('0');
  const [emergencyAmt, setEmergencyAmt] = useState('');

  const load = async () => {
    try {
      const s = read.storage;
      const [baseROI, directRef, binary, minW, deduction, timeUnit, propDepth, genDepth, bal] = await Promise.all([
        s.baseROI(), s.directReferralPercent(), s.binaryMatchPercent(), s.minWithdraw(),
        s.withdrawDeduction(), s.TIME_UNIT(), s.MAX_PROPAGATION_DEPTH(), s.MAX_GENERATION_DEPTH(),
        s.getContractBalance()
      ]);
      const thresholds = await Promise.all([0, 1, 2, 3].map((i) => s.roiThresholds(i)));
      const rates = await Promise.all([0, 1, 2, 3].map((i) => s.roiRates(i)));

      setForm({
        baseROI: bpsToPct(baseROI),
        thresholds: thresholds.map((t) => fromWei(t)),
        rates: rates.map((r) => bpsToPct(r)),
        directRef: bpsToPct(directRef),
        binary: bpsToPct(binary),
        minWithdraw: fromWei(minW),
        deduction: bpsToPct(deduction),
        timeUnit: timeUnit.toString(),
        propDepth: propDepth.toString(),
        genDepth: genDepth.toString()
      });
      setBalance(fromWei(bal));
    } catch (err) {
      console.error('Admin settings load error:', err);
    }
  };

  useEffect(() => { load(); }, [read]);

  if (!form) return <Loading />;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const setArr = (key, i) => (e) => {
    const arr = [...form[key]];
    arr[i] = e.target.value;
    setForm({ ...form, [key]: arr });
  };

  const saveROI = async () => {
    const all = [form.baseROI, ...form.thresholds, ...form.rates];
    if (!all.every(isNum)) return showError('All ROI fields must be valid numbers');
    for (let i = 1; i < 4; i++) {
      if (parseFloat(form.thresholds[i]) < parseFloat(form.thresholds[i - 1])) {
        return showError('Thresholds must be in ascending order');
      }
    }
    const ok = await runTx('roi', 'ROI config update', (w) => w.storage.setROIConfig(
      pctToBps(form.baseROI),
      form.thresholds.map(toWei),
      form.rates.map(pctToBps)
    ));
    if (ok) load();
  };

  const saveIncome = async () => {
    if (!isNum(form.directRef) || !isNum(form.binary)) return showError('Enter valid percentages');
    const ok = await runTx('income', 'Income % update', (w) =>
      w.storage.setIncomePercents(pctToBps(form.directRef), pctToBps(form.binary))
    );
    if (ok) load();
  };

  const saveWithdraw = async () => {
    if (!isNum(form.minWithdraw) || !isNum(form.deduction)) return showError('Enter valid values');
    if (parseFloat(form.deduction) > 100) return showError('Deduction cannot exceed 100%');
    const ok = await runTx('withdraw', 'Withdraw config update', (w) =>
      w.storage.setWithdrawConfig(toWei(form.minWithdraw), pctToBps(form.deduction))
    );
    if (ok) load();
  };

  const saveTimeUnit = async () => {
    const v = parseInt(form.timeUnit, 10);
    if (!v || v < 1) return showError('Time unit must be at least 1 second');
    if (!window.confirm(`Change time unit to ${v} seconds? This changes ROI and binary period length for everyone.`)) return;
    const ok = await runTx('time', 'Time unit update', (w) => w.storage.setTimeUnit(v));
    if (ok) load();
  };

  const saveDepths = async () => {
    const prop = parseInt(form.propDepth, 10);
    const gen = parseInt(form.genDepth, 10);
    if (!prop || prop < 1 || prop > 500) return showError('Binary depth must be 1 - 500');
    if (!gen || gen < 1 || gen > 50) return showError('Generation depth must be 1 - 50');
    const ok = await runTx('depths', 'Depth update', (w) => w.storage.setMaxDepths(prop, gen));
    if (ok) load();
  };

  const emergencyWithdraw = async () => {
    if (!isNum(emergencyAmt) || parseFloat(emergencyAmt) <= 0) return showError('Enter an amount');
    if (parseFloat(emergencyAmt) > parseFloat(balance)) return showError('Amount exceeds contract balance');
    if (!window.confirm(`Withdraw ${emergencyAmt} USDT from the contract to the OWNER wallet?`)) return;
    const ok = await runTx('emergency', 'Emergency withdraw', (w) => w.storage.emergencyWithdraw(toWei(emergencyAmt)));
    if (ok) { setEmergencyAmt(''); load(); }
  };

  return (
    <>
      <Section title="ROI Config" subtitle="Rate per period. Tier applies when direct business ≥ threshold.">
        <Input label="Base ROI" type="number" suffix="%" value={form.baseROI} onChange={set('baseROI')} />
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="admin-row-2">
            <Input label={`Tier ${i + 1} threshold`} type="number" suffix="USDT" value={form.thresholds[i]} onChange={setArr('thresholds', i)} />
            <Input label={`Tier ${i + 1} rate`} type="number" suffix="%" value={form.rates[i]} onChange={setArr('rates', i)} />
          </div>
        ))}
        <Button fullWidth onClick={saveROI} loading={pending === 'roi'}>Save ROI Config</Button>
      </Section>

      <Section title="Income Percents">
        <div className="admin-row-2">
          <Input label="Direct referral" type="number" suffix="%" value={form.directRef} onChange={set('directRef')} />
          <Input label="Binary match" type="number" suffix="%" value={form.binary} onChange={set('binary')} />
        </div>
        <Button fullWidth onClick={saveIncome} loading={pending === 'income'}>Save Income %</Button>
      </Section>

      <Section title="Withdraw Config">
        <div className="admin-row-2">
          <Input label="Min withdraw" type="number" suffix="USDT" value={form.minWithdraw} onChange={set('minWithdraw')} />
          <Input label="Deduction" type="number" suffix="%" value={form.deduction} onChange={set('deduction')} />
        </div>
        <Button fullWidth onClick={saveWithdraw} loading={pending === 'withdraw'}>Save Withdraw Config</Button>
      </Section>

      <Section title="Time Unit" subtitle="Length of one ROI / binary period in seconds.">
        <Input type="number" suffix="sec" value={form.timeUnit} onChange={set('timeUnit')} />
        <div className="admin-chips">
          {TIME_PRESETS.map((p) => (
            <button key={p.value} className="admin-chip" onClick={() => setForm({ ...form, timeUnit: String(p.value) })}>
              {p.label}
            </button>
          ))}
        </div>
        <Button fullWidth onClick={saveTimeUnit} loading={pending === 'time'}>Save Time Unit</Button>
      </Section>

      {isOwner && (
        <Section title="Propagation Depths">
          <div className="admin-row-2">
            <Input label="Binary (1-500)" type="number" value={form.propDepth} onChange={set('propDepth')} />
            <Input label="Generation (1-50)" type="number" value={form.genDepth} onChange={set('genDepth')} />
          </div>
          <Button fullWidth variant="secondary" onClick={saveDepths} loading={pending === 'depths'}>Save Depths</Button>
        </Section>
      )}

      <Section title="Emergency Withdraw" subtitle={`Sends tokens to the owner wallet. Contract balance: ${formatNumber(balance)} USDT`} danger>
        <Input type="number" suffix="USDT" placeholder="Amount" value={emergencyAmt} onChange={(e) => setEmergencyAmt(e.target.value)} />
        <Button fullWidth variant="danger" onClick={emergencyWithdraw} loading={pending === 'emergency'}>Emergency Withdraw</Button>
      </Section>
    </>
  );
};

export default AdminSettings;
